"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CasosDetallados() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="sobre-casos" id="casos" className="bg-background text-foreground py-24 md:py-32 overflow-hidden border-t border-border/30">
      <div className="container mx-auto px-6 md:px-8 max-w-4xl">
        <div ref={ref} className={`mb-20 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-foreground leading-tight mb-6">
            Dos casos.
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed max-w-2xl">
            No los más bonitos. Los más representativos del criterio que aplica Fulcro.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          <CasoUno />
          <CasoDos />
        </div>
      </div>
    </section>;
}
function CasoUno() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <article ref={ref} id="caso-01" className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="font-mono text-xs tracking-widest text-primary uppercase mb-6">
        Caso · 01
      </div>

      <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-foreground leading-tight mb-6">
        Operadora nacional de servicios aeroportuarios.
      </h3>

      <p className="font-serif italic text-xl md:text-2xl text-foreground/80 leading-snug mb-10 max-w-3xl">
        Seis unidades de negocio. Más de 500 colaboradores. Cuentas por cobrar a aerolíneas con ciclos de 60 a 90 días.
      </p>

      <div className="space-y-6 font-sans text-base md:text-lg text-foreground/90 leading-relaxed max-w-3xl">
        <p>
          El sistema interno previo había sido construido por desarrolladores que priorizaron la tecnología sobre la operación. Los reportes salían como archivos Excel que alguien tenía que descargar todos los días, copiar y pegar a un archivo maestro, antes de poder enviar las cuentas por cobrar a las aerolíneas. El proceso tomaba de veinte a veinticinco días entre que el servicio se prestaba y la cuenta llegaba al cliente. En un ciclo de pago a noventa días, eso convierte noventa en ciento doce.
        </p>

        <p>
          Construimos un puente entre el sistema heredado y Monday. La información que antes vivía en archivos Excel desconectados ahora pasa directo a tableros operativos, donde cada unidad de negocio gestiona sus servicios y la facturación se prepara en paralelo.{" "}
          <span className="text-primary">
            El ciclo de cuentas por cobrar bajó de veinte a veinticinco días, a dos o tres.
          </span>
        </p>

        <p>
          En paralelo, encapsulamos otros procesos repetitivos. Las papeletas de servicios especiales — más de dos mil al mes — que antes alguien armaba manualmente en archivos PDF cada mes para enviar a las aerolíneas, ahora se generan automáticamente cada quincena desde formularios capturados por los operadores en piso.
        </p>

        <p>
          Hoy la operación corre con sesenta y un usuarios activos en Monday Enterprise distribuidos en seis unidades de negocio. Los reportes ejecutivos que antes tomaban dos a tres días en armarse ahora salen en tiempo real desde el mismo tablero. Cuentas por pagar está en construcción.
        </p>
      </div>

      <div className="border-l-2 border-primary pl-6 mt-12 max-w-2xl">
        <div className="font-mono text-xs tracking-widest text-primary uppercase mb-3">
          El criterio que aplicó
        </div>
        <p className="font-serif italic text-lg md:text-xl text-foreground leading-snug">
          El sistema heredado no era el problema — el problema era que entregaba archivos cerrados que necesitaban trabajo manual para conectar con el resto de la operación. La solución no fue reemplazarlo, fue construir el puente correcto.
        </p>
      </div>
    </article>;
}
function CasoDos() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <article ref={ref} id="caso-02" className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="font-mono text-xs tracking-widest text-primary uppercase mb-6">
        Caso · 02
      </div>

      <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-foreground leading-tight mb-6">
        Empresa de turismo y experiencias con flotilla mediana.
      </h3>

      <p className="font-serif italic text-xl md:text-2xl text-foreground/80 leading-snug mb-10 max-w-3xl">
        Setenta y dos vehículos en operación. Recopilación manual diaria de formularios de campo. Visibilidad ejecutiva tardía.
      </p>

      <div className="space-y-6 font-sans text-base md:text-lg text-foreground/90 leading-relaxed max-w-3xl">
        <p>
          La operación de flotilla dependía de una persona que recopilaba manualmente formularios de combustible, liberación y devolución de vehículos, y los capturaba uno por uno en Monday. Los reportes para dirección llegaban días después, dependiendo de la disponibilidad del capturista.
        </p>

        <p>
          Empezamos por un frente acotado: conectar el sistema de formularios externo con Monday para que las gráficas que el cliente necesitaba se generaran nativamente, eliminando una licencia adicional que se pagaba solo por esa función. Cuando la licencia del sistema externo se venció, migramos todo a una plataforma que el cliente ya pagaba — Zoho Forms — y eliminamos el costo recurrente.
        </p>

        <p>
          Desde ese punto fuimos expandiendo. Liberación, devolución y control de combustible quedaron bajo el mismo flujo. La persona que capturaba manualmente dejó de ser necesaria. Los reportes a dirección — antes irregulares — hoy llegan al correo del director diario, semanal y mensualmente, según corresponda. La visibilidad ejecutiva que antes era reactiva ahora es continua.
        </p>

        <p><span className="text-primary" data-ast-id="f:/src/pages/About/sections/PagueIpvaMultasELicenciamentoSemSairDeCasam.tsx|sig:p8yc2l" data-ast-start="6524" data-ast-end="6684">Hoy procesan entre ciento cincuenta y doscientos cincuenta formularios diarios sin captura manual.</span> La siguiente fase es mantenimiento preventivo de flotilla, hoy controlado en hojas de excel, pronto encapsulado en el mismo sistema operativo.</p>
      </div>

      <div className="border-l-2 border-primary pl-6 mt-12 max-w-2xl">
        <div className="font-mono text-xs tracking-widest text-primary uppercase mb-3">
          El criterio que aplicó
        </div>
        <p className="font-serif italic text-lg md:text-xl text-foreground leading-snug">
          No automatizamos todo de una vez. Empezamos donde había ahorro inmediato de licencia, y construimos desde ahí. La automatización siguió a la operación, no al revés.
        </p>
      </div>
    </article>;
}